import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { LuArrowUpDown, LuTriangleAlert, LuInfo } from "react-icons/lu";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import ReportTitle from "../../report/ReportTitle";
import { MasonryLayout } from "@/components/common/MasonryLayout";
import { useState, useEffect, useRef, useMemo } from "react";
import axios, { CancelTokenSource } from "axios";
import { BrandAd } from "@/screens/analysis/types";
import { fetchFolderAds, removeAdFromFolder, generateAd } from "../../../apiService";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { ClickableToast } from "@/components/common/toast/ClickableToast";
import { FolderEmptyState } from "@/screens/creative-library/components/EmptyState";
import { useCommentAnalysisStore } from "@/screens/analysis/store";
import MediaTypeDropdown from "@/screens/creatives/components/filters/MediaTypeDropdown";
import BookmarkAd from "../../adActions/BookmarkAd";
import CreateAd, { CreateAdSettings } from "../../adActions/CreateAd";
import SelectAd from "../../adActions/SelectAd";
import AdCard from "@/components/common/adCard";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import { useGlobalStore } from "@/GlobalStore";
import PageLoader from "@/components/common/loaders/PageLoader";
import { buildRoute } from "@/router/constants";
import { IconInfoCircle } from "@tabler/icons-react";
import CreativeInputSection from "@/screens/creative-library/components/common/CreativeInputSection";
import { AspectRatio } from "@/screens/creative-library/components/tabs/AspectRatioDropdown";
import { Variations } from "@/screens/creative-library/components/tabs/VariationsDropdown";
import {
  coerceModel,
  DEFAULT_MODEL,
  Model,
} from "@/screens/creative-library/components/tabs/ModelDropdown";
import { File as CustomFile, LibraryMedia } from "@/types";
import { UploadingFile } from "@/components/hoc/fileAttachment/types";
import { uploadFilesAsync } from "@/api/globalApiService";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  CREATIVE_LIBRARY_STORAGE_KEYS,
} from "@/utils/localStorage";

const CompetitorFolderView = () => {
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedSidebarItem } = useCommentAnalysisStore();
  const { sidebarCollapsed, selectedAccount } = useGlobalStore();
  const navigate = useNavigate();
  const folderId = searchParams.get("folderId");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "running_days">("newest");
  const [folderAds, setFolderAds] = useState<BrandAd[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  // Multi-select state for ads
  const [selectedAdIds, setSelectedAdIds] = useState<string[]>([]);

  // Creative input section state - initialize from localStorage (same as CreateDashboard)
  const [creativePrompt, setCreativePrompt] = useState("");
  const [creativeAspectRatio, setCreativeAspectRatio] = useState<AspectRatio>(
    () =>
      (loadFromLocalStorage(
        CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO,
        "Auto",
      ) as AspectRatio) || "Auto",
  );
  const [creativeVariations, setCreativeVariations] = useState<Variations>(
    () =>
      (loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, 4) as Variations) ||
      4,
  );
  const [creativeModel, setCreativeModel] = useState<Model>(() =>
    coerceModel(loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, DEFAULT_MODEL)),
  );
  const [creativeAvoidDangerZone, setCreativeAvoidDangerZone] = useState(
    () =>
      (loadFromLocalStorage(
        CREATIVE_LIBRARY_STORAGE_KEYS.AVOID_DANGER_ZONE,
        false,
      ) as boolean) || false,
  );
  const [creativeDefaultPromptId, setCreativeDefaultPromptId] = useState<string | null>(
    () =>
      (loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID, null) as
        | string
        | null) || null,
  );
  const [creativeFiles, setCreativeFiles] = useState<CustomFile[]>([]);
  const [creativeLibraryMedia, setCreativeLibraryMedia] = useState<LibraryMedia[]>([]);
  const [creativeUploadingFiles, setCreativeUploadingFiles] = useState<UploadingFile[]>(
    [],
  );
  const [isCreativeFileUploading, setIsCreativeFileUploading] = useState(false);
  const [isCreativeSubmitting, setIsCreativeSubmitting] = useState(false);
  const [creativePopoverOpen, setCreativePopoverOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const creativeFileInputRef = useRef<HTMLInputElement>(null);

  const ATTACHMENT_LIMIT = 5;

  const sortByLabels = {
    newest: "Newest",
    oldest: "Oldest",
    running_days: "Longest Running",
  };

  type SortOption = keyof typeof sortByLabels;

  useEffect(() => {
    const loadFolderAds = async () => {
      if (!accountId || !folderId) return;

      // Cancel previous request if it exists
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel();
      }

      // Create new CancelToken for this request
      cancelTokenRef.current = axios.CancelToken.source();

      setIsLoading(true);
      try {
        const ads = await fetchFolderAds({
          accountId,
          folderId,
          sortType: sortBy,
          creativeType: mediaType ?? undefined,
          cancelToken: cancelTokenRef.current.token,
        });
        setFolderAds(ads);
        setIsLoading(false);
      } catch (error) {
        // Don't log error if request was cancelled
        if (!axios.isCancel(error)) {
          console.error(
            error instanceof Error ? error.message : "Failed to fetch folder ads",
          );
        }
        setIsLoading(false);
      }
    };

    void loadFolderAds();
  }, [accountId, folderId, sortBy, mediaType]);

  // Cleanup effect to cancel request on component unmount
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel();
      }
    };
  }, []);

  const handleBookmark = async (adId: string, folderId: string) => {
    if (!accountId || !folderId) return;

    try {
      await removeAdFromFolder({
        accountId,
        folderId,
        adId,
      });

      // Remove the ad from the local state
      setFolderAds(folderAds.filter((ad) => ad.id !== adId));
    } catch (error) {
      console.error(
        error instanceof Error ? error.message : "Failed to remove ad from folder",
      );
    }
  };

  // Ad selection handlers
  const totalAttachments =
    selectedAdIds.length + creativeFiles.length + creativeLibraryMedia.length;

  const handleAdToggle = (adId: string) => {
    setSelectedAdIds((prev) => {
      const isSelected = prev.includes(adId);
      if (isSelected) {
        return prev.filter((id) => id !== adId);
      } else {
        // Check if we can add more
        if (totalAttachments >= ATTACHMENT_LIMIT) {
          toast.message(
            <ToastContent
              icon={LuInfo}
              headingPrefix="Attachment limit reached"
              subheadingPrefix={`You can only attach up to ${ATTACHMENT_LIMIT} items in total.`}
              isError={false}
              isWarning={true}
            />,
          );
          return prev;
        }
        return [...prev, adId];
      }
    });
  };

  const handleRemoveSelectedAd = (adId: string) => {
    setSelectedAdIds((prev) => prev.filter((id) => id !== adId));
  };

  const handleCreativeFileUpload = async (selectedFiles: globalThis.File[]) => {
    // Enforce overall attachment limit (includes files, library media, and selected ads)
    const capacity =
      ATTACHMENT_LIMIT -
      (selectedAdIds.length + creativeFiles.length + creativeLibraryMedia.length);

    if (selectedFiles.length > capacity) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix={
            capacity === 0 ? "Attachment limit reached" : "Cannot attach more files"
          }
          subheadingPrefix={`You can only attach up to ${ATTACHMENT_LIMIT} items in total.`}
          isError={false}
          isWarning={true}
        />,
      );
      return;
    }

    if (selectedFiles.length > 0) {
      // Create immediate preview items with temp IDs
      const newUploadingItems: UploadingFile[] = selectedFiles.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const preview = URL.createObjectURL(file);
        return { file, preview, id: tempId };
      });

      setCreativeUploadingFiles(newUploadingItems);
      setIsCreativeFileUploading(true);

      try {
        const filesToProcess = selectedFiles.slice(0, capacity);
        const response = await uploadFilesAsync(filesToProcess, accountId || "");
        setCreativeFiles((prev) => {
          // Use a Map to deduplicate by id, always keeping the first occurrence
          const filesById = new Map<string, CustomFile>();
          [...prev, ...response.files].forEach((file) => {
            if (!filesById.has(file.id)) {
              filesById.set(file.id, file);
            }
          });
          return Array.from(filesById.values());
        });
        setCreativeUploadingFiles([]);
        // Clean up object URLs to prevent memory leaks after upload
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } catch (error) {
        console.error("Error uploading files:", error);
        // Clean up uploading files and object URLs on error
        setCreativeUploadingFiles([]);
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } finally {
        setIsCreativeFileUploading(false);
        if (creativeFileInputRef.current) {
          creativeFileInputRef.current.value = "";
        }
      }
    }
  };

  const handleCreativeFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);
      void handleCreativeFileUpload(selectedFiles);
      // Close popover after file attachment
      setCreativePopoverOpen(false);
    }
  };

  const handleCreativeDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleCreativeDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCreativeDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCreativeDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) => {
      const fileType = file.type;
      return (
        fileType.startsWith("image/") ||
        fileType.startsWith("video/") ||
        fileType === "application/pdf"
      );
    });

    if (droppedFiles.length > 0) {
      await handleCreativeFileUpload(droppedFiles);
    }
  };

  const handleCreativePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (imageFiles.length > 0) {
      void handleCreativeFileUpload(imageFiles);
    }
  };

  // Wrapper functions to save to localStorage when settings change
  const handleCreativeAspectRatioChange = (newAspectRatio: AspectRatio) => {
    setCreativeAspectRatio(newAspectRatio);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO, newAspectRatio);
  };

  const handleCreativeVariationsChange = (newVariations: Variations) => {
    setCreativeVariations(newVariations);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, newVariations);
  };

  const handleCreativeModelChange = (newModel: Model) => {
    setCreativeModel(newModel);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, newModel);
  };

  const handleCreativeAvoidDangerZoneChange = (newAvoidDangerZone: boolean) => {
    setCreativeAvoidDangerZone(newAvoidDangerZone);
    saveToLocalStorage(
      CREATIVE_LIBRARY_STORAGE_KEYS.AVOID_DANGER_ZONE,
      newAvoidDangerZone,
    );
  };

  const handleCreativeDefaultPromptIdChange = (promptId: string | null) => {
    setCreativeDefaultPromptId(promptId);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID, promptId);
  };

  // Handler for settings changes from CreateAd
  const handleCreateAdSettingsChange = (settings: {
    model: Model;
    aspectRatio: AspectRatio;
    variations: Variations;
    defaultPromptId: string | null;
    moodboardId: string | null;
  }) => {
    handleCreativeModelChange(settings.model);
    handleCreativeAspectRatioChange(settings.aspectRatio);
    handleCreativeVariationsChange(settings.variations);
    handleCreativeDefaultPromptIdChange(settings.defaultPromptId);
  };

  const handleCreateAd = async (adId: string, settings: CreateAdSettings) => {
    if (!accountId) return;

    try {
      // Find the ad to get its brandId
      const ad = folderAds.find((ad) => ad.id === adId);
      if (!ad) return;

      await generateAd({
        accountId,
        adIds: [adId],
        defaultPromptId: settings.defaultPromptId,
        aspectRatio: settings.aspectRatio,
        numberOfMedia: settings.variations,
        model: settings.model,
        moodboardId: settings.moodboardId ?? null,
      });

      toast.custom((t) => (
        <ClickableToast
          icon={IconInfoCircle}
          iconProps={{
            size: 18,
            className: "text-[#2A94F4]",
          }}
          iconBackgroundClassName="bg-[#2A94F414]"
          hasDescription={true}
          messageHighlight="Ad Creation Started"
          descriptionPrefix="Switch to the Creatives tab to view the created ad."
          onClick={() => {
            if (selectedAccount?.id) {
              navigate(`${buildRoute.create(selectedAccount.id)}?type=image`);
              toast.dismiss(t);
            }
          }}
          onDismiss={() => toast.dismiss(t)}
        />
      ));
    } catch (error) {
      console.error("Failed to generate ad:", error);
      toast.message(
        <ToastContent
          icon={LuTriangleAlert}
          headingPrefix="Failed to create ad"
          subheadingPrefix="Something went wrong. Please try again."
        />,
      );
    }
  };

  const handleCreativeSubmit = async () => {
    if (!accountId || selectedAdIds.length === 0) return;

    // Store values before optimistic update
    const adIdsToSubmit = [...selectedAdIds];
    const promptText = creativePrompt.trim();
    const defaultPromptIdToUse = promptText ? null : creativeDefaultPromptId;
    const aspectRatioToUse = creativeAspectRatio;
    const variationsToUse = creativeVariations;
    const modelToUse = creativeModel;
    const avoidDangerZoneToUse = creativeAvoidDangerZone;
    const filesToUse = [...creativeFiles];
    const libraryMediaToUse = [...creativeLibraryMedia];

    // Optimistically close input section and reset form
    setSelectedAdIds([]);
    setCreativePrompt("");
    setCreativeFiles([]);
    setCreativeLibraryMedia([]);
    setIsCreativeSubmitting(true);

    // Process API in background
    try {
      await generateAd({
        accountId,
        adIds: adIdsToSubmit,
        defaultPromptId: defaultPromptIdToUse,
        prompt: promptText || null,
        aspectRatio: aspectRatioToUse,
        numberOfMedia: variationsToUse,
        model: modelToUse,
        avoidDangerZone: avoidDangerZoneToUse,
        fileIds: filesToUse.map((f) => f.id),
        libraryFileIds: libraryMediaToUse.map((m) => m.file_id),
      });

      toast.custom((t) => (
        <ClickableToast
          icon={IconInfoCircle}
          iconProps={{
            size: 18,
            className: "text-[#2A94F4]",
          }}
          iconBackgroundClassName="bg-[#2A94F414]"
          hasDescription={true}
          messageHighlight="Ad Creation Started"
          descriptionPrefix="Switch to the Creatives tab to view the created ad."
          onClick={() => {
            if (selectedAccount?.id) {
              navigate(`${buildRoute.create(selectedAccount.id)}?type=image`);
              toast.dismiss(t);
            }
          }}
          onDismiss={() => toast.dismiss(t)}
        />
      ));
    } catch (error) {
      console.error("Failed to generate ad:", error);
      toast.message(
        <ToastContent
          icon={LuTriangleAlert}
          headingPrefix="Failed to create ad"
          subheadingPrefix="Something went wrong. Please try again."
          isError
        />,
      );
    } finally {
      setIsCreativeSubmitting(false);
    }
  };

  // Get selected ads data for preview
  const selectedAdsData = useMemo(() => {
    return folderAds.filter((ad) => selectedAdIds.includes(ad.id));
  }, [folderAds, selectedAdIds]);

  if (!accountId || !folderId) {
    return <div>Missing required parameters</div>;
  }

  return (
    <div className="relative flex flex-col w-full h-full pt-[8px] bg-background-gray">
      <div className={`ai-glow-wrapper ai-glow--input pointer-events-none absolute bottom-0 left-0 right-0 z-40 h-[80px] ${isInputFocused ? "ai-glow-active" : ""}`}>
        <div className="ai-glow-edge ai-glow-edge-bottom" />
      </div>
      <div className="w-full flex gap-2 pl-[9px] pr-[14px] mb-[25px]">
        {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}
        <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
          <SelectTrigger className="w-auto h-8 rounded-[8px] border-0 squircle squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray bg-white shadow-sm inline-flex px-[14px] py-[6px] items-center gap-[6px] hover:bg-hover-gray">
            <div className="flex items-center gap-[6px]">
              <LuArrowUpDown
                size={16}
                strokeWidth={1}
                className="text-brand-primary-new shrink-0"
              />
              <span className="mt-[1px] text-[12px] text-brand-primary-new font-normal leading-[19.157px] tracking-[0.24px]">
                {sortByLabels[sortBy]}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent className="rounded-[8px]">
            {(Object.entries(sortByLabels) as [SortOption, string][]).map(
              ([value, label]) => (
                <SelectItem
                  key={value}
                  value={value}
                  className="text-[12px] text-brand-primary-new font-normal leading-[19.157px] tracking-[0.24px]"
                >
                  {label}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        <MediaTypeDropdown
          selectedType={mediaType}
          onChange={setMediaType}
          className="h-8 rounded-[8px] border-0 squircle squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray bg-white shadow-sm hover:bg-hover-gray"
        />
      </div>

      <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden">
        <div className="w-full flex justify-between pl-[9px] pr-[14px]">
          <ReportTitle
            title={selectedSidebarItem?.label ?? ""}
            icon={
              selectedSidebarItem && (
                <selectedSidebarItem.icon size={16} className="text-brand-primary-800" />
              )
            }
            iconBackgroundColor="#F6F6F6"
          />
        </div>

        <Separator className="w-full mt-[19px]" color="#67749B29" />

        <div
          className={`w-full flex-1 p-[9px] ${
            selectedAdIds.length > 0 ? "pb-[200px]" : ""
          }`}
        >
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center">
              <PageLoader />
            </div>
          ) : folderAds.length === 0 ? (
            <FolderEmptyState
              className="-translate-y-[70px] -translate-x-[32px]"
              heading="Folder is Empty"
              description="Add some ads to get started!"
            />
          ) : (
            <MasonryLayout<BrandAd>
              items={folderAds || []}
              renderItem={(ad) => (
                <AdCard
                  header={{
                    title: ad.creative.account_name,
                    icon: ad.creative.account_icon_url || "",
                    meta: (
                      <div className="flex items-center gap-1">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            ad.status === "active" ? "bg-[#039F54]" : "bg-[#E7E8E9]"
                          }`}
                        ></span>
                        <span className="ml-auto text-brand-primary-new text-right text-[12px] font-normal leading-[18px] tracking-[0.24px] relative top-[1px]">
                          {ad.running_days}D
                        </span>
                      </div>
                    ),
                  }}
                  body={{
                    media: {
                      ad_id: ad.id,
                      source: ad.creative?.videos?.[0]
                        ? ad.creative.videos?.[0]?.permalink_url
                        : ad.creative?.images?.[0]?.permalink_url,
                      type: ad.creative?.videos?.[0] ? "video" : "image",
                      thumbnail: ad.creative.thumbnail_url,
                      actions: [
                        // Select action - top left
                        ...(ad.creative.creative_type === "image"
                          ? [
                              {
                                component: (
                                  <SelectAd
                                    isSelected={selectedAdIds.includes(ad.id)}
                                    onToggle={() => handleAdToggle(ad.id)}
                                    disabled={
                                      !selectedAdIds.includes(ad.id) &&
                                      totalAttachments >= ATTACHMENT_LIMIT
                                    }
                                  />
                                ),
                                position: "top-left" as const,
                                visibility: selectedAdIds.includes(ad.id)
                                  ? ("always" as const)
                                  : ("hover" as const),
                              },
                            ]
                          : []),
                        {
                          component: (
                            <DownloadAction
                              url={
                                ad.creative.creative_type === "video"
                                  ? ad.creative?.videos?.[0]?.permalink_url
                                  : ad.creative?.images?.[0]?.uncompressed_url ||
                                    ad.creative?.images?.[0]?.permalink_url
                              }
                              type={ad.creative.creative_type}
                              accountName={ad.creative.account_name}
                            />
                          ),
                          position: "top-right",
                          visibility: "hover",
                        },
                        {
                          component: (
                            <BookmarkAd
                              ad={ad}
                              onBookmark={(folderId) =>
                                void handleBookmark(ad.id, folderId)
                              }
                              folderId={folderId}
                            />
                          ),
                          position: "top-right",
                          visibility: "always",
                        },
                        // Reimagine action - only show when not selected
                        ...(ad.creative.creative_type === "image" &&
                        !selectedAdIds.includes(ad.id)
                          ? [
                              {
                                component: (
                                  <CreateAd
                                    ad={ad}
                                    onCreateAd={(settings) =>
                                      handleCreateAd(ad.id, settings)
                                    }
                                    onSelectAd={() => handleAdToggle(ad.id)}
                                    currentModel={creativeModel}
                                    currentAspectRatio={creativeAspectRatio}
                                    currentVariations={creativeVariations}
                                    currentDefaultPromptId={creativeDefaultPromptId}
                                    onSettingsChange={handleCreateAdSettingsChange}
                                  />
                                ),
                                position: "bottom-left" as const,
                                visibility: "hover" as const,
                              },
                            ]
                          : []),
                      ],
                    },
                  }}
                />
              )}
              cardWidth={296}
              cardGap={12}
              maxColumns={3}
            />
          )}
        </div>
      </div>

      {/* Creative Input Section - shown when ads are selected */}
      {selectedAdIds.length > 0 && (
        <div className="absolute bottom-0 left-1/2 translate-x-[-50%] z-50 w-full">
          <div className="max-w-[750px] mx-auto px-4 py-3">
            <CreativeInputSection
              selectedAds={selectedAdsData.map((ad) => ({
                id: ad.id,
                thumbnailUrl: ad.creative.thumbnail_url,
                accountName: ad.creative.account_name,
                accountIconUrl: ad.creative.account_icon_url,
              }))}
              onRemoveSelectedAd={handleRemoveSelectedAd}
              prompt={creativePrompt}
              creativeType="image"
              aspectRatio={creativeAspectRatio}
              variations={creativeVariations}
              avoidDangerZone={creativeAvoidDangerZone}
              model={creativeModel}
              files={creativeFiles}
              libraryMedia={creativeLibraryMedia}
              uploadingFiles={creativeUploadingFiles}
              isFileUploading={isCreativeFileUploading}
              submitting={isCreativeSubmitting}
              popoverOpen={creativePopoverOpen}
              onPromptChange={setCreativePrompt}
              onCreativeTypeChange={() => {}}
              showCreativeTypeDropdown={false}
              onAspectRatioChange={handleCreativeAspectRatioChange}
              onVariationsChange={handleCreativeVariationsChange}
              onAvoidDangerZoneChange={handleCreativeAvoidDangerZoneChange}
              onModelChange={handleCreativeModelChange}
              onFileChange={handleCreativeFileChange}
              onLibraryAttachmentChange={setCreativeLibraryMedia}
              onRemoveFile={(file) =>
                setCreativeFiles((prev) => prev.filter((f) => f.id !== file.id))
              }
              onRemoveLibraryAttachment={(file) =>
                setCreativeLibraryMedia((prev) =>
                  prev.filter((m) => m.file_id !== file.id),
                )
              }
              onPaste={handleCreativePaste}
              onDragOver={handleCreativeDragOver}
              onDragEnter={handleCreativeDragEnter}
              onDragLeave={handleCreativeDragLeave}
              onDrop={handleCreativeDrop}
              onSubmit={handleCreativeSubmit}
              onPopoverOpenChange={setCreativePopoverOpen}
              fileInputRef={creativeFileInputRef}
              creativeTypeDisabled
              containerClassName="!p-3 !pt-2"
              onFocusChange={setIsInputFocused}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorFolderView;
